import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { inject, Injectable } from '@angular/core';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';

interface Translation {
  [key: string]: string | Translation;
}

@Injectable()
export class MultiTranslateHttpLoader extends TranslateHttpLoader {

  private httpClient: HttpClient = inject(HttpClient);

  private configuration = inject(NimmstaCommonsModuleConfiguration);

  private translationFiles: string[] = [];

  constructor() {
    super();
    this.translationFiles = this.configuration.translationFiles || ['assets/i18n/'];
  }

  public override getTranslation(lang: string): Observable<Translation> {
    const resources = this.translationFiles.map((file) => ({ prefix: file, suffix: '.json' }));
    return forkJoin(
      resources.map((config) => this.httpClient.get<Translation>(`${config.prefix}${lang}${config.suffix}`)),
    ).pipe(
      map((responses) => responses.reduce((merged, current) => this.deepMerge(merged, current), {} as Translation)),
    );
  }

  private deepMerge(target: Translation, source: Translation): Translation {
    const result: Translation = { ...target };

    Object.keys(source).forEach((key) => {
      const targetValue = target[key];
      const sourceValue = source[key];
      if (!sourceValue) {
        return;
      } else if (!targetValue) {
        result[key] = sourceValue;
        return;
      }
      // sourceValue and targetValue are both defined

      if (this.isTranslationObject(sourceValue) && this.isTranslationObject(targetValue)) {
        result[key] = this.deepMerge(targetValue, sourceValue);
      } else if (this.isTranslationObject(sourceValue)) {
        result[key] = sourceValue;
      } else {
        result[key] = targetValue;
      }
    });

    return result;
  }

  private isTranslationObject(value: unknown): value is Translation {
    return (
      value !== null
      && typeof value === 'object'
      && !(value instanceof Array)
      && !this.isPrimitive(value)
    );
  }

  private isPrimitive(value: unknown): boolean {
    const type = typeof value;
    return value === null
      || type === 'string'
      || type === 'number'
      || type === 'boolean';
  }
}
